import {
    type Dispatch,
    type FC,
    type RefObject,
    type SetStateAction,
    useRef,
} from "react";
import { type DragSourceMonitor, useDrop } from "react-dnd";
import { type GridCursorType } from "./Cursor/GridCursor";
import { GRID_CURSOR_HANDLE_TYPE, GRID_CURSOR_MOVE_TYPE, GridCursorDirection } from "./Cursor/GridCursorHandle";
import { GRID_DROP_DEPTH } from "./GridEditor";

export type GridDragItemFactory = (monitor: DragSourceMonitor) => null | GridDragItemType;

export interface GridDragItemType {
    selected: string;
    cursor: RefObject<GridCursorType>;
    setCursor: Dispatch<SetStateAction<GridCursorType>>;
    anchor: {
        top: number;
        left: number;
    };
}

export interface GridDragHandleItemType extends GridDragItemType {
    direction: number;
}

function isGridDragHandleItem(item: GridDragItemType): item is GridDragHandleItemType {
    return "direction" in item && typeof item.direction === "number";
}

interface Props {
    width: number;
    height: number;
}

export const GridDropLayer: FC<Props> = ({
    width,
    height,
}) => {
    const wrapperRef = useRef<HTMLDivElement>(null);

    const [{ dragging }, drop ] = useDrop<GridDragItemType, void, { dragging: boolean; }>(() => ({
        accept : [ GRID_CURSOR_HANDLE_TYPE, GRID_CURSOR_MOVE_TYPE ],
        collect: monitor => ({
            dragging: monitor.getItem() !== null,
        }),
        hover: (item: GridDragItemType, monitor) => {
            const type = monitor.getItemType();

            const initialClientOffset = monitor.getInitialClientOffset();
            const clientOffset = monitor.getClientOffset();

            if(!initialClientOffset || !clientOffset || !wrapperRef.current || !item.cursor.current) {
                return;
            }

            const wrapperBounding = wrapperRef.current.getBoundingClientRect();

            let offsetX = -wrapperBounding.left;
            let offsetY = -wrapperBounding.top;

            if(type === GRID_CURSOR_MOVE_TYPE) {
                offsetX = item.anchor.left + offsetX - initialClientOffset.x;
                offsetY = item.anchor.top + offsetY - initialClientOffset.y;
            }

            const positionX = clientOffset.x + offsetX;
            const positionY = clientOffset.y + offsetY;

            const gridX = Math.round((positionX * width) / wrapperBounding.width);
            const gridY = Math.round((positionY * height) / wrapperBounding.height);

            if(type === GRID_CURSOR_MOVE_TYPE) {
                const top = Math.min(height - item.cursor.current.height, Math.max(0, gridY)) + 1;
                const left = Math.min(width - item.cursor.current.width, Math.max(0, gridX)) + 1;

                const newCursor = {
                    ...item.cursor.current,
                    left,
                    top,
                };

                // Note: Validity is checked in GridCursor.tsx
                item.setCursor(newCursor);
            } else if(type === GRID_CURSOR_HANDLE_TYPE && isGridDragHandleItem(item)) {
                const newCursor = {
                    ...item.cursor.current,
                };

                if(item.direction & GridCursorDirection.LEFT) {
                    const newWidth = item.cursor.current.left + item.cursor.current.width - (gridX + 1);

                    if(newWidth > 0) {
                        newCursor.width = newWidth;
                        newCursor.left = gridX + 1;
                    }
                } else if(item.direction & GridCursorDirection.RIGHT) {
                    newCursor.width = Math.max(gridX - newCursor.left + 1, 1);
                }

                if(item.direction & GridCursorDirection.TOP) {
                    const newHeight = item.cursor.current.top + item.cursor.current.height - (gridY + 1);

                    if(newHeight > 0) {
                        newCursor.height = newHeight;
                        newCursor.top = gridY + 1;
                    }
                } else if(item.direction & GridCursorDirection.BOTTOM) {
                    newCursor.height = Math.max(gridY - newCursor.top + 1, 1);
                }

                // Note: Validity is checked in GridCursor.tsx
                item.setCursor(newCursor);
            }
        },
    }), [ width, height ]);

    return (
        <div
            ref={ref => {
                (wrapperRef.current as any) = drop(ref);
            }}
            id="grid-drop-layer"
            className={
                "absolute top-0 left-0 right-0 bottom-0" +
                (dragging ? "" : " hidden")
            }
            style={{
                zIndex: GRID_DROP_DEPTH,
            }}
        />
    );
};
